import { useEffect, useLayoutEffect, useRef, useState } from "react"

import { useHelpOptional } from "@/components/help/help-provider"
import { Button } from "@/components/ui/button"
import type { Tour } from "@/lib/tours"

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

/**
 * One step at a time, pointed at a real element.
 *
 * The card is positioned **away from** the thing it describes — below it when
 * there is room underneath, above it when there is not. On a 390px screen a
 * tooltip that covers the element it is explaining is worse than no tooltip,
 * and that is the failure mode people remember tours for.
 *
 * Keyboard-first: arrows and Enter advance, ESC exits, and focus is put on the
 * card so a screen reader reads the step rather than announcing nothing. ESC
 * records `skipped`, not `completed` — pressing it means "not now", and a tour
 * that treats being busy as being finished never gets seen again by the people
 * who most needed it.
 */
export function TourSpotlight({
  tour,
  onFinish,
}: {
  tour: Tour
  onFinish: (state: "completed" | "skipped") => void
}) {
  const [index, setIndex] = useState(0)
  const [rect, setRect] = useState<Rect | null>(null)
  const card = useRef<HTMLDivElement>(null)
  const help = useHelpOptional()

  const step = tour.steps[index]
  const last = index === tour.steps.length - 1

  // Where the anchor is, recomputed on resize and scroll. An anchor that is not
  // in the DOM leaves `rect` null and the card centres itself — the step still
  // reads, rather than the tour dying on a missing element.
  useLayoutEffect(() => {
    const measure = () => {
      const target = document.querySelector(`[data-tour="${step.anchor}"]`)
      if (!target) return setRect(null)

      target.scrollIntoView({ block: "center", behavior: "smooth" })
      const box = target.getBoundingClientRect()
      setRect({ top: box.top, left: box.left, width: box.width, height: box.height })
    }

    measure()
    window.addEventListener("resize", measure)
    window.addEventListener("scroll", measure, true)
    return () => {
      window.removeEventListener("resize", measure)
      window.removeEventListener("scroll", measure, true)
    }
  }, [step.anchor])

  useEffect(() => card.current?.focus(), [index])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        onFinish("skipped")
      } else if (event.key === "ArrowRight" || event.key === "Enter") {
        event.preventDefault()
        if (last) onFinish("completed")
        else setIndex((i) => i + 1)
      } else if (event.key === "ArrowLeft") {
        event.preventDefault()
        setIndex((i) => Math.max(0, i - 1))
      }
    }

    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [last, onFinish])

  // Below the anchor when there is room, above it when there is not. Never on
  // top of it.
  const below = rect ? rect.top + rect.height + 12 : 0
  const roomBelow = rect ? window.innerHeight - below > 200 : true
  const cardStyle: React.CSSProperties = rect
    ? {
        position: "fixed",
        top: roomBelow ? below : undefined,
        bottom: roomBelow ? undefined : window.innerHeight - rect.top + 12,
        left: Math.max(12, Math.min(rect.left, window.innerWidth - 340)),
        width: 320,
      }
    : { position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", width: 320 }

  return (
    <>
      {/*
        The dimmer. Click-through is deliberately off — a tour you can click
        past by accident is a tour whose steps get skipped without being read —
        but ESC always exits, so nobody is trapped.
      */}
      <div
        className="fixed inset-0 z-50 bg-black/40 motion-safe:transition-opacity"
        aria-hidden
        onClick={() => onFinish("skipped")}
      />

      {/* The ring around the anchor. Drawn, not a border on the element, so it
          cannot disturb the layout underneath it. */}
      {rect && (
        <div
          className="ring-primary pointer-events-none fixed z-50 rounded-lg ring-2 ring-offset-2 motion-safe:transition-all"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
          }}
          aria-hidden
        />
      )}

      <div
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-label={`${tour.title}: step ${index + 1} of ${tour.steps.length}`}
        tabIndex={-1}
        style={cardStyle}
        className="bg-card border-border focus-visible:ring-ring/50 z-50 rounded-lg border p-4 shadow-lg outline-none focus-visible:ring-[3px]"
      >
        {/* The step counter only. This used to read "AROUND YOUR DESK · 1/5",
            which spent the widest line on the card repeating a name the reader
            has no use for mid-tour; the tour is still named in the help centre
            listing and in this dialog's aria-label. */}
        <p className="text-muted-foreground text-xs">
          {index + 1}/{tour.steps.length}
        </p>
        <h2 className="mt-1.5 text-lg leading-tight font-medium">{step.title}</h2>
        <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">{step.body}</p>

        {step.help && help && (
          <button
            type="button"
            onClick={() => help.open(step.help!)}
            className="text-muted-foreground mt-2 text-xs underline-offset-4 hover:underline"
          >
            Read more
          </button>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => onFinish("skipped")}
            className="text-muted-foreground text-xs hover:underline"
          >
            Skip
          </button>

          <div className="flex items-center gap-1.5">
            {index > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setIndex((i) => i - 1)}>
                Back
              </Button>
            )}
            <Button size="sm" onClick={() => (last ? onFinish("completed") : setIndex((i) => i + 1))}>
              {last ? "Done" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
